import { SubmitEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Button, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { Skeleton } from "@material-ui/lab";
import ComponentList from "components/ComponentList";
import { ShowMoreBtn } from "components/List";
import apiRequest from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import Item from "./Item";
import { ListSearchContainer } from "../Styled";

import { SecurityPolicy } from "@dashboard-v3/api";

const listLimit = 10;

export default function SecurityPoliciesList() {
  const { t } = useTranslation("securityPolicies");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<"ALL" | "MORE" | "ITEM">();
  const [loadingItem, setLoadingItem] = useState<string>();
  const [securityPolicies, setSecurityPolicies] = useState<SecurityPolicy[]>();
  const [searchOffset, setSearchOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const searchTermRef = useRef<string>(undefined);

  async function fetchPolicies(offset = 0): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append("offset", offset.toString());
    queryParams.append("limit", listLimit.toString());

    if (!isBlank(searchTermRef.current)) {
      queryParams.append("partialName", searchTermRef.current);
    }

    try {
      const data = await apiRequest<SecurityPolicy[]>(
        "GET",
        `/security-policies?${queryParams.toString()}`
      );
      setHasMore(data.length === listLimit);
      setSecurityPolicies(prevPolicies => {
        const policies = offset ? [...prevPolicies, ...data] : data;
        return policies.sort(
          (a: SecurityPolicy, b: SecurityPolicy): number =>
            b.updatedAt - a.updatedAt
        );
      });
    } catch (error: unknown) {
      enqueueSnackbar(t("common:errors.fetchError"), {
        variant: "error",
      });
    }
  }

  const loadMore = async () => {
    setLoading("MORE");
    const newOffset = searchOffset + listLimit;
    await fetchPolicies(newOffset);
    setSearchOffset(newOffset);
    setLoading(null);
  };

  useEffect(() => {
    async function initialFetch() {
      setLoading("ALL");
      await fetchPolicies();
      setLoading(null);
    }

    if (!securityPolicies) {
      initialFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSearch(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const searchValue = formData.get("searchValue")?.toString() || "";
    searchTermRef.current = searchValue;
    setSearchOffset(0);
    setLoading("ALL");
    await fetchPolicies(0);
    setLoading(null);
  }

  return (
    <>
      <form onSubmit={onSearch}>
        <ListSearchContainer>
          <TextField
            name="searchValue"
            label={t("list.searchInput.label")}
            size="small"
            fullWidth
            variant="outlined"
            disabled={loading === "ALL"}
            inputProps={{ "data-testid": "sp-list-search-input" }}
            InputProps={{ style: { backgroundColor: "#fff" } }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            disabled={loading === "ALL"}
          >
            <SearchIcon fontSize="small" />
          </Button>
        </ListSearchContainer>
      </form>
      <ComponentList
        list={securityPolicies}
        emptyMsg={t("emptyList")}
        loading={loading === "ALL"}
        renderItems={policy =>
          loadingItem === policy.id ? (
            <Skeleton
              key={policy.id}
              height="4rem"
              animation="wave"
              variant="rect"
            />
          ) : (
            <Item
              key={policy.id}
              fetchPolicies={fetchPolicies}
              policy={policy}
              setLoading={setLoading}
              setLoadingItem={setLoadingItem}
            />
          )
        }
      />
      {hasMore && (
        <ShowMoreBtn loading={loading === "MORE"} onClick={loadMore} />
      )}
    </>
  );
}
